import { createWithEqualityFn } from 'zustand/traditional';

import { useAuthStore } from './auth.store';

/**
 * Which server sign-in has been validated in this page load. Not persisted:
 * a restored sign-in is only trusted again after `useServerAuthenticated`
 * checked it, so the restored play queue does not resolve streams, probe
 * transcoding or retry audio for a session that may already be gone.
 */
interface SessionReadyState {
    validatedServerId: null | string;
}

export const useSessionReadyStore = createWithEqualityFn<SessionReadyState>()(() => ({
    validatedServerId: null,
}));

/** The sign-in of `serverId` was just checked against the server. */
export const markSessionValidated = (serverId: string, generation: number) => {
    const auth = useAuthStore.getState();
    if (auth.currentServer?.id !== serverId || auth.sessionGeneration !== generation) return;
    useSessionReadyStore.setState({ validatedServerId: serverId });
};

export const clearSessionValidated = () => {
    if (useSessionReadyStore.getState().validatedServerId === null) return;
    useSessionReadyStore.setState({ validatedServerId: null });
};

/** Is the current sign-in of `serverId` validated? */
export const isSessionValidatedFor = (serverId: null | string | undefined): boolean =>
    Boolean(serverId) && useSessionReadyStore.getState().validatedServerId === serverId;

export const useValidatedServerId = () => useSessionReadyStore((state) => state.validatedServerId);

// Logout, a rejected sign-in or another server ends the validation with it.
useAuthStore.subscribe((state, previous) => {
    const validated = useSessionReadyStore.getState().validatedServerId;
    if (
        validated &&
        (state.currentServer?.id !== validated ||
            state.sessionGeneration !== previous.sessionGeneration)
    )
        clearSessionValidated();
});
